import { useCallback, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';

import type { ManagedBlockConfiguration, ManagedBlockConfigurationUpdate } from '../configuration';

import { getSourceAppFromUrl, getSourceUrlsFromEntries, isValidSourceUrl } from './configuration';
import {
	getNextSourceEntries,
	removeSourceEntry,
	replaceSourceEntry,
} from './source-entry-updates';
import type { ManagedBlockSource, ManagedBlockSourceMode } from './types';

type UseSourceInputArgs = {
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	onSourcesAdded?: () => void;
	sourceEntries: ManagedBlockSource[];
	sourceMode: ManagedBlockSourceMode;
	sourceUrls: string;
};

type UseSourceInputResult = {
	handleRemoveSourceUrl: (sourceUrl: string) => void;
	handleRetrySourceUrl: (sourceUrl: string) => void;
	handleSourceInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handleSourceInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
	handleSourceInputPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
	handleSourceModeChange: (sourceMode: ManagedBlockSourceMode) => void;
	showSourceLimitError: boolean;
	sourceInputValue: string;
};

export const useSourceInput = ({
	onChange,
	onConfigEdit,
	onInteraction,
	onSourcesAdded,
	sourceEntries,
	sourceMode,
	sourceUrls,
}: UseSourceInputArgs): UseSourceInputResult => {
	const [sourceInputValue, setSourceInputValue] = useState('');
	const [showSourceLimitError, setShowSourceLimitError] = useState(false);

	const handleSourceModeChange = useCallback(
		(nextSourceMode: ManagedBlockSourceMode): void => {
			onInteraction();

			if (
				sourceMode !== nextSourceMode ||
				sourceUrls.length > 0 ||
				sourceEntries.length > 0 ||
				sourceInputValue.length > 0
			) {
				onConfigEdit();
			}

			setSourceInputValue('');
			setShowSourceLimitError(false);
			onChange(
				(currentConfiguration): ManagedBlockConfiguration => ({
					...currentConfiguration,
					sourceEntries: [],
					sourceMode: nextSourceMode,
					sourceUrls: '',
				}),
			);
		},
		[
			onChange,
			onConfigEdit,
			onInteraction,
			sourceEntries.length,
			sourceInputValue,
			sourceMode,
			sourceUrls,
		],
	);

	const handleSourceInputChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			onInteraction();
			setShowSourceLimitError(false);
			setSourceInputValue(event.currentTarget.value);
		},
		[onInteraction],
	);

	const addManualSources = useCallback(
		(rawSourceUrls: string): void => {
			onInteraction();
			const { entries: nextSourceEntries, limitExceeded } = getNextSourceEntries(
				sourceEntries,
				rawSourceUrls,
			);

			setShowSourceLimitError(limitExceeded);

			if (nextSourceEntries.length === sourceEntries.length) {
				if (!limitExceeded) {
					setSourceInputValue('');
				}
				return;
			}

			onSourcesAdded?.();
			onConfigEdit();
			onChange((currentConfiguration): ManagedBlockConfiguration => {
				const next = getNextSourceEntries(
					currentConfiguration.sourceEntries,
					rawSourceUrls,
				).entries;

				return {
					...currentConfiguration,
					sourceEntries: next,
					sourceUrls: getSourceUrlsFromEntries(next),
				};
			});
			setSourceInputValue('');
		},
		[onChange, onConfigEdit, onInteraction, onSourcesAdded, sourceEntries],
	);

	const handleSourceInputKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>): void => {
			if (event.key === 'Enter') {
				event.preventDefault();
				addManualSources(sourceInputValue);
			}
		},
		[addManualSources, sourceInputValue],
	);

	const handleSourceInputPaste = useCallback(
		(event: ClipboardEvent<HTMLInputElement>): void => {
			const pastedText = event.clipboardData.getData('text');
			if (pastedText.trim().length === 0) {
				return;
			}

			event.preventDefault();
			addManualSources(pastedText);
		},
		[addManualSources],
	);

	const handleRemoveSourceUrl = useCallback(
		(sourceUrl: string): void => {
			onInteraction();
			const nextSourceEntries = removeSourceEntry(sourceEntries, sourceUrl);

			if (sourceEntries.length !== nextSourceEntries.length) {
				onConfigEdit();
			}

			setShowSourceLimitError(false);
			onChange((currentConfiguration): ManagedBlockConfiguration => {
				const next = removeSourceEntry(currentConfiguration.sourceEntries, sourceUrl);

				return {
					...currentConfiguration,
					sourceEntries: next,
					sourceUrls: getSourceUrlsFromEntries(next),
				};
			});
		},
		[onChange, onConfigEdit, onInteraction, sourceEntries],
	);

	const handleRetrySourceUrl = useCallback(
		(sourceUrl: string): void => {
			onInteraction();
			setShowSourceLimitError(false);
			onChange((currentConfiguration): ManagedBlockConfiguration => {
				const isValidRetryUrl = isValidSourceUrl(sourceUrl);
				const nextSourceEntries = replaceSourceEntry(
					currentConfiguration.sourceEntries,
					sourceUrl,
					{
						app: getSourceAppFromUrl(sourceUrl),
						invalidReason: isValidRetryUrl ? undefined : 'invalid-url',
						status: isValidRetryUrl ? 'resolving' : 'invalid',
					},
				);

				return {
					...currentConfiguration,
					sourceEntries: nextSourceEntries,
					sourceUrls: getSourceUrlsFromEntries(nextSourceEntries),
				};
			});
		},
		[onChange, onInteraction],
	);

	return {
		sourceInputValue,
		showSourceLimitError,
		handleRemoveSourceUrl,
		handleRetrySourceUrl,
		handleSourceInputChange,
		handleSourceInputKeyDown,
		handleSourceInputPaste,
		handleSourceModeChange,
	};
};
